import api from './axios';
import { createAsyncDataCache } from './async-data-cache.mjs';

const cache = createAsyncDataCache();
export const resetAdminData = () => cache.invalidatePrefix('');

const normalizeText = (value) => (value || '').trim();

const usersKey = (params = {}) => [
  'users',
  Number(params.page) || 1,
  normalizeText(params.search),
  normalizeText(params.subscription_plan),
].join(':');

const servicesKey = (search = '') => `services:${normalizeText(search)}`;

export const peekUsersData = (params) => cache.peek(usersKey(params));

export const loadUsersData = (params = {}, options = {}) => {
  const normalizedParams = {
    page: Number(params.page) || 1,
    search: normalizeText(params.search),
    subscription_plan: normalizeText(params.subscription_plan) || undefined,
  };

  return cache.load(
    usersKey(normalizedParams),
    () => api.get('/admin/users/', { params: normalizedParams }).then((response) => {
      const data = response.data;
      const responsePage = Number(data.pagination?.page);
      if (responsePage !== normalizedParams.page) {
        throw new Error(`分页响应不一致：请求第 ${normalizedParams.page} 页，返回第 ${responsePage || '未知'} 页`);
      }

      const responseEmails = (data.data || []).map((user) => user.email?.toLowerCase());
      const hasDuplicateAdjacentPage = [
        normalizedParams.page - 1,
        normalizedParams.page + 1,
      ]
        .filter((page) => page >= 1)
        .some((page) => {
          const adjacent = cache.peek(usersKey({ ...normalizedParams, page }))?.data;
          const adjacentEmails = (adjacent?.data || []).map((user) => user.email?.toLowerCase());
          return responseEmails.length > 0
            && responseEmails.length === adjacentEmails.length
            && responseEmails.every((email, index) => email === adjacentEmails[index]);
        });

      if (hasDuplicateAdjacentPage) {
        throw new Error('分页接口返回了重复用户，请稍后重试');
      }
      return data;
    }),
    options,
  );
};

export const invalidateUsersData = () => cache.invalidatePrefix('users:');

export const warmAdjacentUsersData = (params, pagination) => {
  const currentPage = Number(params.page) || 1;
  const totalPages = Number(pagination.total_pages) || 1;
  const adjacentPages = [currentPage - 1, currentPage + 1]
    .filter((page) => page >= 1 && page <= totalPages);

  return Promise.allSettled(
    adjacentPages.map((page) => loadUsersData({ ...params, page })),
  );
};

export const peekServicesData = (search = '') => cache.peek(servicesKey(search));

export const loadServicesData = (search = '', options = {}) => {
  const normalizedSearch = normalizeText(search);
  return cache.load(
    servicesKey(normalizedSearch),
    () => api.get('/admin/services/', {
      params: normalizedSearch ? { search: normalizedSearch } : undefined,
    }).then((response) => ({
      ...response.data,
      data: (response.data.data || []).filter(
        (service) => service.service_key !== 'autosquare_website',
      ),
    })),
    options,
  );
};

export const invalidateServicesData = () => cache.invalidatePrefix('services:');

export const warmAdminData = () => Promise.allSettled([
  loadUsersData({ page: 1, search: '' }),
  loadServicesData(''),
]);
